import { SqliteEntityBridge } from '@infra/persistence/SqliteEntityBridge';

export type SyncAction = 'create' | 'update' | 'delete';

export interface SyncOutboxRow {
  id: string;
  tenant_id: string;
  resource: string;
  action: SyncAction;
  entity_id: string;
  payload: Record<string, unknown>;
  base_version: number;
  created_at: string;
  /** « rejected » : refusée définitivement par le serveur, mise de côté (jamais perdue). */
  status?: 'pending' | 'rejected';
  rejected_code?: string;
  rejected_message?: string;
  rejected_at?: string;
}

export interface RemoteCacheRow {
  id: string;
  tenant_id: string;
  resource: string;
  entity_id: string;
  payload: Record<string, unknown>;
  remote_version: number;
  dirty: boolean;
  updated_at: string;
}

export interface SyncConflictRow {
  id: string;
  tenant_id: string;
  resource: string;
  entity_id: string;
  local_payload: Record<string, unknown>;
  local_version: number;
  remote_payload: Record<string, unknown>;
  remote_version: number;
  detected_at: string;
}

const OUTBOX = 'sync_outbox' as const;
const CACHE = 'remote_cache' as const;

/** Identifiant de ligne du cache : préfixé par l'entreprise (deux entreprises, deux lignes). */
function cacheId(tenantId: string, resource: string, entityId: string): string {
  return `${tenantId}::${resource}::${entityId}`;
}

/** Ancien identifiant (sans entreprise) : lu en repli, supprimé à la prochaine écriture. */
function legacyCacheId(resource: string, entityId: string): string {
  return `${resource}::${entityId}`;
}

/** Lit l'ancienne ligne; la ligne d'une autre entreprise (garde TENANT_MISMATCH) compte comme absente. */
async function readLegacyCacheRow(
  resource: string,
  entityId: string,
): Promise<RemoteCacheRow | null> {
  try {
    return await SqliteEntityBridge.get<RemoteCacheRow>(CACHE, legacyCacheId(resource, entityId));
  } catch {
    return null;
  }
}

/** File d'attente et cache local chiffrés pour le mode multi-postes. */
export class RemoteSyncStore {
  static async isEnabled(): Promise<boolean> {
    return SqliteEntityBridge.isAvailable() && (await SqliteEntityBridge.isSqlitePrimary());
  }

  static async listCache<T extends { id: string }>(
    tenantId: string,
    resource: string,
  ): Promise<T[]> {
    const rows = await SqliteEntityBridge.list<RemoteCacheRow>(CACHE, tenantId);
    return rows
      .filter((r) => r.resource === resource)
      .map(
        (r) => ({ ...(r.payload as object), id: r.entity_id, tenant_id: tenantId }) as unknown as T,
      );
  }

  /** Fiches modifiées localement et pas encore acquittées par le serveur (jamais écrasées par une lecture). */
  static async listDirtyCache<T extends { id: string }>(
    tenantId: string,
    resource: string,
  ): Promise<Array<T & { _remoteVersion?: number; _dirty?: boolean }>> {
    const rows = await SqliteEntityBridge.list<RemoteCacheRow>(CACHE, tenantId);
    return rows
      .filter((r) => r.resource === resource && r.dirty)
      .map(
        (r) =>
          ({
            ...(r.payload as object),
            id: r.entity_id,
            tenant_id: tenantId,
            _remoteVersion: r.remote_version,
            _dirty: true,
          }) as unknown as T & { _remoteVersion?: number; _dirty?: boolean },
      );
  }

  static async getCache<T extends { id: string }>(
    tenantId: string,
    resource: string,
    entityId: string,
  ): Promise<(T & { _remoteVersion?: number; _dirty?: boolean }) | null> {
    let row = await SqliteEntityBridge.get<RemoteCacheRow>(
      CACHE,
      cacheId(tenantId, resource, entityId),
    );
    if (!row) {
      const legacy = await readLegacyCacheRow(resource, entityId);
      row = legacy && legacy.tenant_id === tenantId ? legacy : null;
    }
    if (!row || row.tenant_id !== tenantId) return null;
    return {
      ...(row.payload as object),
      id: row.entity_id,
      tenant_id: tenantId,
      _remoteVersion: row.remote_version,
      _dirty: row.dirty,
    } as unknown as T & { _remoteVersion?: number; _dirty?: boolean };
  }

  static async putCache(
    tenantId: string,
    resource: string,
    entityId: string,
    payload: Record<string, unknown>,
    remoteVersion: number,
    dirty: boolean,
  ): Promise<void> {
    const row: RemoteCacheRow = {
      id: cacheId(tenantId, resource, entityId),
      tenant_id: tenantId,
      resource,
      entity_id: entityId,
      payload,
      remote_version: remoteVersion,
      dirty,
      updated_at: new Date().toISOString(),
    };
    await SqliteEntityBridge.put(CACHE, row);
    await RemoteSyncStore.dropLegacyCache(tenantId, resource, entityId);
  }

  static async removeCache(tenantId: string, resource: string, entityId: string): Promise<void> {
    await SqliteEntityBridge.delete(CACHE, cacheId(tenantId, resource, entityId));
    await RemoteSyncStore.dropLegacyCache(tenantId, resource, entityId);
  }

  /** Supprime l'ancienne ligne (id sans entreprise) uniquement si elle appartient à CETTE entreprise. */
  private static async dropLegacyCache(
    tenantId: string,
    resource: string,
    entityId: string,
  ): Promise<void> {
    const legacy = await readLegacyCacheRow(resource, entityId);
    if (legacy && legacy.tenant_id === tenantId) {
      await SqliteEntityBridge.delete(CACHE, legacyCacheId(resource, entityId));
    }
  }

  static async enqueue(
    entry: Omit<SyncOutboxRow, 'id' | 'created_at'> & { id?: string },
  ): Promise<void> {
    const row: SyncOutboxRow = {
      id: entry.id ?? crypto.randomUUID(),
      tenant_id: entry.tenant_id,
      resource: entry.resource,
      action: entry.action,
      entity_id: entry.entity_id,
      payload: entry.payload,
      base_version: entry.base_version,
      created_at: new Date().toISOString(),
    };
    await SqliteEntityBridge.put(OUTBOX, row);
  }

  static async listOutbox(tenantId: string): Promise<SyncOutboxRow[]> {
    const rows = await SqliteEntityBridge.list<SyncOutboxRow>(OUTBOX, tenantId);
    return rows.sort((a, b) => a.created_at.localeCompare(b.created_at));
  }

  /** Entrées en attente de rejeu (hors entrées refusées). */
  static async listPendingOutbox(tenantId: string): Promise<SyncOutboxRow[]> {
    return (await RemoteSyncStore.listOutbox(tenantId)).filter((r) => r.status !== 'rejected');
  }

  /** Entrées refusées par le serveur, conservées pour consultation / correction. */
  static async listRejectedOutbox(tenantId: string): Promise<SyncOutboxRow[]> {
    return (await RemoteSyncStore.listOutbox(tenantId)).filter((r) => r.status === 'rejected');
  }

  /** Met l'entrée de côté avec le motif du refus : visible, jamais supprimée silencieusement. */
  static async markOutboxRejected(
    entry: SyncOutboxRow,
    code: string | undefined,
    message: string,
  ): Promise<void> {
    await SqliteEntityBridge.put(OUTBOX, {
      ...entry,
      status: 'rejected',
      rejected_code: code ?? 'refused',
      rejected_message: message,
      rejected_at: new Date().toISOString(),
    } satisfies SyncOutboxRow);
  }

  static async removeOutbox(id: string): Promise<void> {
    await SqliteEntityBridge.delete(OUTBOX, id);
  }

  static async saveConflict(conflict: Omit<SyncConflictRow, 'id' | 'detected_at'>): Promise<void> {
    const row: SyncConflictRow = {
      ...conflict,
      id: crypto.randomUUID(),
      detected_at: new Date().toISOString(),
    };
    await SqliteEntityBridge.put('module_data', {
      id: `${conflict.tenant_id}::sync.conflict.${conflict.resource}.${conflict.entity_id}`,
      tenant_id: conflict.tenant_id,
      namespace: 'sync.conflict',
      value: row,
    });
  }

  static async listConflicts(tenantId: string): Promise<SyncConflictRow[]> {
    const rows = await SqliteEntityBridge.list<{
      id: string;
      tenant_id: string;
      namespace: string;
      value: SyncConflictRow;
    }>('module_data', tenantId);
    return rows.filter((r) => r.namespace === 'sync.conflict').map((r) => r.value);
  }

  static async clearConflict(tenantId: string, resource: string, entityId: string): Promise<void> {
    await SqliteEntityBridge.delete(
      'module_data',
      `${tenantId}::sync.conflict.${resource}.${entityId}`,
    );
  }
}
